import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaceDetector, FilesetResolver, Detection } from '@mediapipe/tasks-vision';

/**
 * A blur region stored in normalised image coordinates (0..1) so it maps
 * cleanly onto the on-screen preview (at any zoom) AND the full-resolution
 * export canvas. `source` records whether the box came from face detection or
 * was drawn by the user: detected faces are blurred as a feathered ellipse
 * (face-shaped, soft edges); manual boxes stay rectangular.
 */
interface BlurRegion {
    id: string;
    x: number;      // 0..1 — left edge as a fraction of image width
    y: number;      // 0..1 — top edge as a fraction of image height
    w: number;      // 0..1 — width as a fraction of image width
    h: number;      // 0..1 — height as a fraction of image height
    blurred: boolean;
    source: 'detected' | 'manual';
    // Once the user moves/resizes (or for manual draws), the box IS the oval
    // bounds — skip the auto "grow to whole head" applied to raw detections.
    adjusted?: boolean;
}

interface PhotoRedactorProps {
    show: boolean;
    /** The newly-selected file to redact (use this OR imageUrl). */
    file?: File;
    /** An already-uploaded image URL to re-edit. Must be CORS-readable to export. */
    imageUrl?: string;
    /** Called with the flattened, redacted image as a JPEG File. */
    onApply: (redactedFile: File) => void;
    onCancel: () => void;
}

// Hosted MediaPipe assets. Swap these for self-hosted copies (in /public) if you
// don't want a runtime CDN dependency.
const WASM_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';
const FACE_MODEL = 'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite';

type RedactStyle = 'blur' | 'pixelate';
type Corner = 'nw' | 'ne' | 'sw' | 'se';

/**
 * Geometry of a region in W×H canvas pixels. Detected face boxes are tight on
 * the eyes/nose/mouth, so we grow them to cover the whole head (forehead, chin,
 * hair) and nudge slightly upward — otherwise a blurred face sits next to a
 * sharp hairline, which looks pasted-on. Manual boxes are used as drawn.
 */
function regionGeom(r: BlurRegion, W: number, H: number) {
    const cw = r.w * W;
    const ch = r.h * H;
    const cx = (r.x + r.w / 2) * W;
    if (r.source === 'manual' || r.adjusted) {
        return { cx, cy: (r.y + r.h / 2) * H, rw: cw, rh: ch };
    }
    return {
        cx,
        cy: (r.y + r.h / 2) * H - ch * 0.07,
        rw: cw * 1.22,
        rh: ch * 1.38,
    };
}

/**
 * Bake a detected face's grown display-geometry into its own box and mark it
 * `adjusted`, so resize/move handles line up with the oval the user sees and
 * further edits use the box verbatim. No-op for manual/already-adjusted. (0..1)
 */
function bakeGeom(r: BlurRegion): BlurRegion {
    if (r.source === 'manual' || r.adjusted) return r;
    const rw = r.w * 1.22;
    const rh = r.h * 1.38;
    const cx = r.x + r.w / 2;
    const cy = r.y + r.h / 2 - r.h * 0.07;
    return { ...r, x: cx - rw / 2, y: cy - rh / 2, w: rw, h: rh, adjusted: true };
}

/**
 * Paint an obscured patch for one region onto `ctx` (already holding the sharp
 * image), W×H px. Builds the effect on a throwaway canvas and masks it to a
 * soft-feathered ellipse (faces) or rectangle (manual) so it blends seamlessly.
 *  - 'blur'     → smooth Gaussian, radius = strength × the larger dimension.
 *  - 'pixelate' → mosaic; block size scales with strength (downscale→upscale).
 */
function paintRegionBlur(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    r: BlurRegion,
    W: number,
    H: number,
    strength: number,
    style: RedactStyle,
) {
    const { cx, cy, rw, rh } = regionGeom(r, W, H);
    if (rw <= 0 || rh <= 0) return;

    const tmp = document.createElement('canvas');
    tmp.width = Math.max(1, Math.ceil(W));
    tmp.height = Math.max(1, Math.ceil(H));
    const tctx = tmp.getContext('2d');
    if (!tctx) return;

    // 1) Build the obscured copy of the whole image.
    if (style === 'pixelate') {
        // Downscale then upscale with smoothing OFF → crisp mosaic blocks.
        const px = Math.max(3, Math.max(rw, rh) * strength * 0.85);
        const sw = Math.max(1, Math.round(W / px));
        const sh = Math.max(1, Math.round(H / px));
        const small = document.createElement('canvas');
        small.width = sw;
        small.height = sh;
        const sctx = small.getContext('2d');
        if (!sctx) return;
        sctx.imageSmoothingEnabled = true;
        sctx.drawImage(img, 0, 0, sw, sh);
        tctx.imageSmoothingEnabled = false;
        tctx.drawImage(small, 0, 0, sw, sh, 0, 0, W, H);
        tctx.imageSmoothingEnabled = true;
    } else {
        const blurPx = Math.max(4, Math.max(rw, rh) * strength);
        tctx.filter = `blur(${blurPx}px)`;
        tctx.imageSmoothingQuality = 'high';
        tctx.drawImage(img, 0, 0, W, H);
        tctx.filter = 'none';
    }

    // 2) Keep only a soft-feathered ellipse of the region — opaque core, gentle
    //    fade to transparent so the edge melts into the photo (no disc seam).
    //    Detected faces AND manually-drawn areas are both treated as ovals, so a
    //    region drawn over a missed face looks just like an auto-detected one.
    tctx.globalCompositeOperation = 'destination-in';
    tctx.save();
    tctx.translate(cx, cy);
    tctx.scale(rw / 2, rh / 2);
    const grad = tctx.createRadialGradient(0, 0, 0.2, 0, 0, 1);
    grad.addColorStop(0, 'rgba(0,0,0,1)');
    grad.addColorStop(0.6, 'rgba(0,0,0,1)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    tctx.fillStyle = grad;
    tctx.beginPath();
    tctx.arc(0, 0, 1, 0, Math.PI * 2);
    tctx.fill();
    tctx.restore();

    // 3) Stamp the masked patch back over the sharp image.
    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(tmp, 0, 0);
}

const PhotoRedactor: React.FC<PhotoRedactorProps> = ({ show, file, imageUrl, onApply, onCancel }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const naturalSize = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
    const stageRef = useRef<HTMLDivElement>(null);
    // Kept alive after the initial pass so double-click can re-detect on demand.
    const detectorRef = useRef<FaceDetector | null>(null);

    const [regions, setRegions] = useState<BlurRegion[]>([]);
    const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);
    const [stageSize, setStageSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
    // zoom = user multiplier on top of the fit-to-stage scale.
    const [zoom, setZoom] = useState(1);
    // Blur radius / mosaic block size as a fraction of the region (tunable).
    const [blurStrength, setBlurStrength] = useState(0.15);
    const [style, setStyle] = useState<RedactStyle>('blur');
    const [status, setStatus] = useState<'loading' | 'detecting' | 'ready' | 'error'>('loading');
    const [statusMessage, setStatusMessage] = useState('');
    const [drawMode, setDrawMode] = useState(false);

    // Currently selected region (shows resize handles) and the in-progress gesture.
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const actionRef = useRef<{
        type: 'draw' | 'move' | 'resize';
        corner?: Corner;
        startNX: number;
        startNY: number;
        orig: { x: number; y: number; w: number; h: number };
        targetId?: string;
        moved: boolean;
        wasBlurred?: boolean;
        rect?: BlurRegion | null;
    } | null>(null);

    // Scale that fits the WHOLE image inside the available stage area (= 100% / "Fit").
    // The stage fills almost the entire screen, so the image is large by default.
    const fitScale = useMemo(() => {
        if (!imgDims || stageSize.w <= 0 || stageSize.h <= 0) return 1;
        const pad = 24; // breathing room around the image
        return Math.min((stageSize.w - pad) / imgDims.w, (stageSize.h - pad) / imgDims.h, 1);
    }, [imgDims, stageSize]);

    // Allow zooming up to native resolution (at least 3× for small images).
    const maxZoom = Math.max(3, Math.min(8, fitScale > 0 ? 1 / fitScale : 3));

    // ── Load the image and run face detection when the modal opens ──
    useEffect(() => {
        if (!show) return;
        let cancelled = false;
        // Object URLs we create and must revoke on cleanup: the picked File
        // (local) and/or a blob we fetch for a remote (S3) image — see below.
        let localFileUrl: string | null = file ? URL.createObjectURL(file) : null;
        let fetchedBlobUrl: string | null = null;

        setStatus('loading');
        setRegions([]);
        setSelectedId(null);
        setZoom(1);

        // Decode `src` into the <img> we detect/blur on. `src` is always a
        // same-origin blob/object URL by the time it reaches here, so the canvas
        // is never tainted and export (toBlob) works without crossOrigin.
        const loadInto = (src: string) => {
            const img = new Image();
            img.onload = async () => {
                if (cancelled) return;
                imageRef.current = img;
                naturalSize.current = { w: img.naturalWidth, h: img.naturalHeight };
                setImgDims({ w: img.naturalWidth, h: img.naturalHeight });
                await detectFaces(img);
            };
            img.onerror = () => {
                if (cancelled) return;
                setStatus('error');
                setStatusMessage('Could not load the image.');
            };
            img.src = src;
        };

        if (localFileUrl) {
            loadInto(localFileUrl);
        } else if (imageUrl) {
            // Don't point <img crossOrigin> straight at the S3 URL: a plain display
            // <img> may have already cached this URL WITHOUT cors headers (the request
            // sent no Origin, so S3 returned no Access-Control-Allow-Origin). The
            // browser then reuses that poisoned cache entry for the crossOrigin load
            // and fails CORS. Fetching with `cache: 'reload'` forces a fresh CORS
            // request, and the resulting blob is same-origin so the canvas stays clean.
            (async () => {
                try {
                    const resp = await fetch(imageUrl, { mode: 'cors', cache: 'reload' });
                    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                    const blob = await resp.blob();
                    if (cancelled) return;
                    fetchedBlobUrl = URL.createObjectURL(blob);
                    loadInto(fetchedBlobUrl);
                } catch (err) {
                    if (cancelled) return;
                    console.error('Could not load image for editing', err);
                    setStatus('error');
                    setStatusMessage('Could not load this photo for editing (it may be blocked by a cross-origin/CORS restriction).');
                }
            })();
        }

        return () => {
            cancelled = true;
            if (localFileUrl) URL.revokeObjectURL(localFileUrl);
            if (fetchedBlobUrl) URL.revokeObjectURL(fetchedBlobUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, file, imageUrl]);

    // Track the stage size so the image can fit/fill the available space.
    useEffect(() => {
        if (!show) return;
        const el = stageRef.current;
        if (!el) return;
        const update = () => setStageSize({ w: el.clientWidth, h: el.clientHeight });
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [show]);

    const detectFaces = async (img: HTMLImageElement) => {
        setStatus('detecting');
        try {
            const vision = await FilesetResolver.forVisionTasks(WASM_BASE);
            detectorRef.current?.close();
            const detector = await FaceDetector.createFromOptions(vision, {
                baseOptions: { modelAssetPath: FACE_MODEL },
                runningMode: 'IMAGE',
            });
            detectorRef.current = detector;          // keep alive for double-click re-detect
            const result = detector.detect(img);

            const { w: nw, h: nh } = naturalSize.current;
            const detected: BlurRegion[] = result.detections
                .map((d: Detection, i: number): BlurRegion | null => {
                    const box = d.boundingBox;
                    if (!box) return null;
                    return {
                        id: `face-${i}`,
                        x: box.originX / nw,
                        y: box.originY / nh,
                        w: box.width / nw,
                        h: box.height / nh,
                        // Default OFF: the target child is usually the subject, so the
                        // user opts in to blurring the others rather than un-blurring.
                        blurred: false,
                        source: 'detected',
                    };
                })
                .filter((r): r is BlurRegion => r !== null);

            setRegions(detected);
            setStatus('ready');
            setStatusMessage(
                detected.length
                    ? `Found ${detected.length} face${detected.length > 1 ? 's' : ''}. Tap to blur; double-click a missed face to auto-blur it; drag to move, grab a corner to resize, Delete to remove.`
                    : 'No faces detected — double-click a face to auto-blur it (or “Draw blur area”), then drag/resize.'
            );
        } catch (err) {
            // Detection is best-effort; manual drawing still works if it fails.
            console.error('Face detection failed', err);
            setStatus('ready');
            setStatusMessage('Auto face-detection unavailable — use “Draw blur area” to blur manually.');
        }
    };

    // ── Drawing the preview canvas ──
    const redraw = useCallback(() => {
        const canvas = canvasRef.current;
        const img = imageRef.current;
        if (!canvas || !img) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { w: nw, h: nh } = naturalSize.current;
        // Render the backing store at the sharpest useful resolution for the
        // current zoom (capped at the image's native size); the CSS size below
        // controls the visual zoom and may upscale small images further.
        const renderScale = Math.min(1, fitScale * zoom);
        const W = Math.max(1, Math.round(nw * renderScale));
        const H = Math.max(1, Math.round(nh * renderScale));
        canvas.width = W;
        canvas.height = H;
        canvas.style.width = `${nw * fitScale * zoom}px`;
        canvas.style.height = `${nh * fitScale * zoom}px`;

        ctx.clearRect(0, 0, W, H);
        ctx.drawImage(img, 0, 0, W, H);

        // Bake the blur for each blurred region (skip the live drag preview —
        // we only outline that until it's committed, to keep dragging smooth).
        regions
            .filter(r => r.blurred && r.id !== 'drag-preview')
            .forEach(r => paintRegionBlur(ctx, img, r, W, H, blurStrength, style));

        // Outline guides (PREVIEW ONLY — never exported) so you can see which
        // ovals are blurred. Red = blurred, blue dashed = a detected face that
        // isn't blurred yet (tap it), grey dashed = a manual area being drawn.
        regions.forEach(r => {
            const g = regionGeom(r, W, H);
            ctx.lineWidth = 2;
            ctx.strokeStyle = r.blurred ? 'rgba(220,53,69,0.85)' : (r.source === 'detected' ? 'rgba(13,110,253,0.9)' : 'rgba(108,117,125,0.9)');
            ctx.setLineDash(r.blurred ? [] : [6, 4]);
            ctx.beginPath();
            ctx.ellipse(g.cx, g.cy, g.rw / 2, g.rh / 2, 0, 0, Math.PI * 2);
            ctx.stroke();
        });
        ctx.setLineDash([]);

        // Resize handles on the selected oval (corners of its bounding box).
        if (selectedId) {
            const sel = regions.find(r => r.id === selectedId);
            if (sel) {
                const g = regionGeom(sel, W, H);
                const hs = Math.max(9, Math.min(22, W * 0.013));
                const corners: [number, number][] = [
                    [g.cx - g.rw / 2, g.cy - g.rh / 2],
                    [g.cx + g.rw / 2, g.cy - g.rh / 2],
                    [g.cx - g.rw / 2, g.cy + g.rh / 2],
                    [g.cx + g.rw / 2, g.cy + g.rh / 2],
                ];
                corners.forEach(([hx, hy]) => {
                    ctx.fillStyle = '#ffffff';
                    ctx.strokeStyle = '#0d6efd';
                    ctx.lineWidth = 2;
                    ctx.beginPath();
                    ctx.rect(hx - hs / 2, hy - hs / 2, hs, hs);
                    ctx.fill();
                    ctx.stroke();
                });
            }
        }
    }, [regions, fitScale, zoom, blurStrength, style, selectedId]);

    useEffect(() => { redraw(); }, [redraw]);

    // Keyboard: Delete/Backspace removes the selected oval; Escape closes.
    useEffect(() => {
        if (!show) return;
        const onKey = (e: KeyboardEvent) => {
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
                setRegions(prev => prev.filter(r => r.id !== selectedId));
                setSelectedId(null);
            } else if (e.key === 'Escape') {
                onCancel();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [show, selectedId, onCancel]);

    // Free the detector when the component unmounts.
    useEffect(() => () => { detectorRef.current?.close(); detectorRef.current = null; }, []);

    // ── Pointer interaction (all in 0..1 coords, so it's correct at any zoom /
    //    backing resolution). Tap = blur/unblur; drag a region = move it; drag a
    //    corner handle = resize/reshape; "Draw blur area" = add a new oval. ──
    const HANDLE_HIT_PX = 18;
    const MIN_SIZE = 0.02;

    const cornerAt = (r: BlurRegion, nx: number, ny: number, tx: number, ty: number): Corner | null => {
        const near = (px: number, py: number) => Math.abs(nx - px) <= tx && Math.abs(ny - py) <= ty;
        if (near(r.x, r.y)) return 'nw';
        if (near(r.x + r.w, r.y)) return 'ne';
        if (near(r.x, r.y + r.h)) return 'sw';
        if (near(r.x + r.w, r.y + r.h)) return 'se';
        return null;
    };

    const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const cr = e.currentTarget.getBoundingClientRect();
        const nx = (e.clientX - cr.left) / cr.width;
        const ny = (e.clientY - cr.top) / cr.height;
        const act = actionRef.current;

        if (!act) {
            // Hover feedback only — set the cursor without triggering a re-render.
            if (status === 'ready' && !drawMode) {
                const tx = HANDLE_HIT_PX / cr.width;
                const ty = HANDLE_HIT_PX / cr.height;
                const sel = selectedId ? regions.find(r => r.id === selectedId) : undefined;
                const corner = sel ? cornerAt(bakeGeom(sel), nx, ny, tx, ty) : null;
                let cursor = 'pointer';
                if (corner) cursor = (corner === 'nw' || corner === 'se') ? 'nwse-resize' : 'nesw-resize';
                else if (regions.some(r => nx >= r.x && nx <= r.x + r.w && ny >= r.y && ny <= r.y + r.h)) cursor = 'move';
                e.currentTarget.style.cursor = cursor;
            }
            return;
        }

        const dx = nx - act.startNX;
        const dy = ny - act.startNY;
        if (Math.abs(dx) > 0.004 || Math.abs(dy) > 0.004) act.moved = true;

        if (act.type === 'draw') {
            const rect: BlurRegion = {
                id: 'drag-preview',
                x: Math.min(act.startNX, nx),
                y: Math.min(act.startNY, ny),
                w: Math.abs(nx - act.startNX),
                h: Math.abs(ny - act.startNY),
                blurred: true,
                source: 'manual',
                adjusted: true,
            };
            act.rect = rect;
            setRegions(prev => [...prev.filter(r => r.id !== 'drag-preview'), rect]);
            return;
        }

        if (act.type === 'move') {
            const x = Math.max(0, Math.min(1 - act.orig.w, act.orig.x + dx));
            const y = Math.max(0, Math.min(1 - act.orig.h, act.orig.y + dy));
            setRegions(prev => prev.map(r => r.id === act.targetId ? { ...r, x, y } : r));
            return;
        }

        if (act.type === 'resize') {
            let { x, y, w, h } = act.orig;
            const right = x + w;
            const bottom = y + h;
            if (act.corner === 'se') { w = nx - x; h = ny - y; }
            else if (act.corner === 'nw') { x = nx; y = ny; w = right - nx; h = bottom - ny; }
            else if (act.corner === 'ne') { y = ny; w = nx - x; h = bottom - ny; }
            else if (act.corner === 'sw') { x = nx; w = right - nx; h = ny - y; }
            w = Math.max(MIN_SIZE, w);
            h = Math.max(MIN_SIZE, h);
            x = Math.max(0, Math.min(x, 1 - MIN_SIZE));
            y = Math.max(0, Math.min(y, 1 - MIN_SIZE));
            w = Math.min(w, 1 - x);
            h = Math.min(h, 1 - y);
            setRegions(prev => prev.map(r => r.id === act.targetId ? { ...r, x, y, w, h } : r));
            return;
        }
    };

    const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (status !== 'ready') return;
        const cr = e.currentTarget.getBoundingClientRect();
        const nx = (e.clientX - cr.left) / cr.width;
        const ny = (e.clientY - cr.top) / cr.height;

        if (drawMode) {
            e.currentTarget.setPointerCapture(e.pointerId);
            actionRef.current = { type: 'draw', startNX: nx, startNY: ny, orig: { x: 0, y: 0, w: 0, h: 0 }, moved: false, rect: null };
            return;
        }

        const tx = HANDLE_HIT_PX / cr.width;
        const ty = HANDLE_HIT_PX / cr.height;

        // 1) Resize: grab a corner of the currently-selected region.
        if (selectedId) {
            const sel = regions.find(r => r.id === selectedId);
            if (sel) {
                const baked = bakeGeom(sel);
                const corner = cornerAt(baked, nx, ny, tx, ty);
                if (corner) {
                    if (baked !== sel) setRegions(prev => prev.map(r => r.id === sel.id ? baked : r));
                    e.currentTarget.setPointerCapture(e.pointerId);
                    actionRef.current = { type: 'resize', corner, startNX: nx, startNY: ny, orig: { x: baked.x, y: baked.y, w: baked.w, h: baked.h }, targetId: sel.id, moved: false };
                    return;
                }
            }
        }

        // 2) Select + (potentially) move the topmost region under the point.
        const hit = [...regions].reverse().find(r => nx >= r.x && nx <= r.x + r.w && ny >= r.y && ny <= r.y + r.h);
        if (hit) {
            const baked = bakeGeom(hit);
            setRegions(prev => prev.map(r => r.id === hit.id ? { ...baked, blurred: true } : r));
            setSelectedId(hit.id);
            e.currentTarget.setPointerCapture(e.pointerId);
            actionRef.current = { type: 'move', startNX: nx, startNY: ny, orig: { x: baked.x, y: baked.y, w: baked.w, h: baked.h }, targetId: hit.id, moved: false, wasBlurred: hit.blurred };
            return;
        }

        // 3) Empty space → deselect.
        setSelectedId(null);
    };

    const handlePointerUp = () => {
        const act = actionRef.current;
        actionRef.current = null;
        if (!act) return;

        if (act.type === 'draw') {
            const rect = act.rect;
            setDrawMode(false);
            const valid = !!rect && rect.w > 0.02 && rect.h > 0.02;
            const id = valid ? `manual-${Date.now()}` : null;
            setRegions(prev => {
                const committed = prev.filter(r => r.id !== 'drag-preview');
                if (valid && id && rect) committed.push({ ...rect, id });
                return committed;
            });
            if (id) setSelectedId(id);
            return;
        }

        // A tap (no real drag) on an already-blurred region → unblur + deselect.
        if (act.type === 'move' && !act.moved && act.wasBlurred) {
            setRegions(prev => prev.map(r => r.id === act.targetId ? { ...r, blurred: false } : r));
            setSelectedId(null);
        }
    };

    // Double-click: re-detect a face near the click (on a crop, so small/missed
    // faces are far more likely to be found), blur it, and select it to tweak.
    const findFaceAt = (nx: number, ny: number) => {
        const img = imageRef.current;
        const { w: nw, h: nh } = naturalSize.current;
        if (!img || nw === 0 || nh === 0) return;

        let placed: BlurRegion | null = null;
        const det = detectorRef.current;
        if (det) {
            const win = Math.max(nw, nh) * 0.22;          // crop window (natural px) around the click
            const cxp = nx * nw;
            const cyp = ny * nh;
            const sx = Math.max(0, Math.min(nw - win, cxp - win / 2));
            const sy = Math.max(0, Math.min(nh - win, cyp - win / 2));
            const sw = Math.min(win, nw - sx);
            const sh = Math.min(win, nh - sy);
            const scale = 320 / Math.max(sw, sh);         // upscale crop so the face is detector-sized
            const crop = document.createElement('canvas');
            crop.width = Math.max(1, Math.round(sw * scale));
            crop.height = Math.max(1, Math.round(sh * scale));
            const cctx = crop.getContext('2d');
            if (cctx) {
                cctx.drawImage(img, sx, sy, sw, sh, 0, 0, crop.width, crop.height);
                try {
                    const res = det.detect(crop);
                    const clickX = (cxp - sx) * scale;
                    const clickY = (cyp - sy) * scale;
                    let best: { originX: number; originY: number; width: number; height: number } | null = null;
                    let bestDist = Infinity;
                    for (const d of res.detections) {
                        const b = d.boundingBox;
                        if (!b) continue;
                        const dist = Math.hypot(b.originX + b.width / 2 - clickX, b.originY + b.height / 2 - clickY);
                        if (dist < bestDist) { bestDist = dist; best = b; }
                    }
                    if (best) {
                        placed = bakeGeom({
                            id: 'tmp',
                            x: (sx + best.originX / scale) / nw,
                            y: (sy + best.originY / scale) / nh,
                            w: (best.width / scale) / nw,
                            h: (best.height / scale) / nh,
                            blurred: true,
                            source: 'detected',
                        });
                    }
                } catch { /* fall through to a default oval */ }
            }
        }

        // Fallback: a default oval centred on the click, ready to resize.
        if (!placed) {
            const dw = 0.12;
            const dh = 0.16;
            placed = {
                id: 'tmp',
                x: Math.max(0, Math.min(1 - dw, nx - dw / 2)),
                y: Math.max(0, Math.min(1 - dh, ny - dh / 2)),
                w: dw,
                h: dh,
                blurred: true,
                source: 'manual',
                adjusted: true,
            };
        }

        const id = `face-dc-${Date.now()}`;
        setRegions(prev => [...prev, { ...(placed as BlurRegion), id }]);
        setSelectedId(id);
    };

    const handleDoubleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (status !== 'ready') return;
        const cr = e.currentTarget.getBoundingClientRect();
        findFaceAt((e.clientX - cr.left) / cr.width, (e.clientY - cr.top) / cr.height);
    };

    const blurAllFaces = () => setRegions(prev => prev.map(r => ({ ...r, blurred: true })));
    const clearAll = () => {
        setSelectedId(null);
        setRegions(prev => prev.map(r =>
            r.source === 'manual' ? null : { ...r, blurred: false }
        ).filter((r): r is BlurRegion => r !== null));
    };
    const removeSelected = () => {
        if (!selectedId) return;
        setRegions(prev => prev.filter(r => r.id !== selectedId));
        setSelectedId(null);
    };

    const zoomIn = () => setZoom(z => Math.min(maxZoom, +(z * 1.4).toFixed(3)));
    const zoomOut = () => setZoom(z => Math.max(1, +(z / 1.4).toFixed(3)));
    const zoomFit = () => setZoom(1);

    // ── Export at full resolution ──
    const handleApply = () => {
        const img = imageRef.current;
        if (!img) return;
        const { w: nw, h: nh } = naturalSize.current;
        const out = document.createElement('canvas');
        out.width = nw;
        out.height = nh;
        const ctx = out.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(img, 0, 0, nw, nh);
        regions
            .filter(r => r.blurred && r.id !== 'drag-preview')
            .forEach(r => paintRegionBlur(ctx, img, r, nw, nh, blurStrength, style));

        out.toBlob(
            (blob) => {
                if (!blob) return;
                const baseName = (file?.name || 'photo').replace(/\.[^.]+$/, '');
                onApply(new File([blob], `${baseName}_redacted.jpg`, { type: 'image/jpeg' }));
            },
            'image/jpeg',
            0.92
        );
    };

    if (!show) return null;

    const busy = status === 'loading' || status === 'detecting';

    const zoomPct = Math.round(fitScale * zoom * 100);

    // Portal to <body> so the overlay is positioned against the viewport, not a
    // transformed/scrolled ancestor (which would push the toolbar off-screen).
    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Blur faces"
            style={{
                position: 'fixed',
                inset: 0,
                height: '100dvh',
                zIndex: 2000,
                background: '#262420',          // warm charcoal so photos pop, still matches the cream theme
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {/* ── Top bar ── */}
            <div className="ivory-panel d-flex align-items-center justify-content-between px-3 py-2"
                style={{ position: 'relative', zIndex: 1, borderBottom: '1px solid rgba(13,77,26,0.12)' }}>
                <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                    <h6 className="m-0 fw-semibold" style={{ color: '#2a6b3a' }}>
                        <i className="fas fa-user-secret me-2"></i>Blur faces
                    </h6>
                    <span className="small text-muted text-truncate d-none d-lg-inline" style={{ maxWidth: '46vw' }}>
                        {busy ? 'Loading…' : statusMessage}
                    </span>
                </div>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}
                    aria-label="Close editor" title="Close without saving (Esc)">
                    <i className="fas fa-times"></i>
                </button>
            </div>

            {/* ── Stage ── */}
            <div ref={stageRef} style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'auto' }}>
                {busy && (
                    <div className="position-absolute top-50 start-50 translate-middle" style={{ zIndex: 3 }}>
                        <div className="spinner-border text-light" role="status">
                            <span className="visually-hidden">Loading…</span>
                        </div>
                    </div>
                )}
                <div style={{ minWidth: '100%', minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
                    <canvas
                        ref={canvasRef}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onDoubleClick={handleDoubleClick}
                        style={{
                            display: 'block',
                            touchAction: 'none',
                            cursor: drawMode ? 'crosshair' : 'pointer',
                            opacity: busy ? 0.3 : 1,
                            boxShadow: '0 8px 30px rgba(0,0,0,0.55)',
                            borderRadius: 6,
                        }}
                    />
                </div>

                {/* Floating zoom pill — sits over the photo, out of the toolbar's way */}
                {!busy && (
                    <div className="ivory-panel position-absolute d-inline-flex align-items-center gap-1 px-2 py-1"
                        style={{ right: 14, bottom: 14, zIndex: 3, borderRadius: 999, boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
                        <button type="button" className="btn btn-sm btn-light" disabled={zoom <= 1} onClick={zoomOut} title="Zoom out" aria-label="Zoom out">
                            <i className="fas fa-search-minus"></i>
                        </button>
                        <span className="small fw-semibold" style={{ minWidth: '3.2em', textAlign: 'center' }}>{zoomPct}%</span>
                        <button type="button" className="btn btn-sm btn-light" disabled={zoom >= maxZoom} onClick={zoomIn} title="Zoom in" aria-label="Zoom in">
                            <i className="fas fa-search-plus"></i>
                        </button>
                        <button type="button" className="btn btn-sm btn-light" disabled={zoom === 1} onClick={zoomFit} title="Fit photo to screen">
                            Fit
                        </button>
                    </div>
                )}
            </div>

            {/* Status hint on small screens (top-bar hint is hidden there) */}
            <div className="d-lg-none px-3 py-1 small text-center text-muted" style={{ background: '#faf8f1' }}>
                {busy ? 'Loading…' : statusMessage}
            </div>

            {/* ── Bottom toolbar: tools on the left, primary actions on the right ── */}
            <div className="ivory-panel d-flex flex-wrap align-items-center gap-2 px-3 py-2"
                style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(13,77,26,0.12)' }}>

                {/* Style */}
                <div className="btn-group btn-group-sm" role="group" aria-label="Redaction style">
                    <button type="button" className={`btn ${style === 'blur' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        disabled={status !== 'ready'} onClick={() => setStyle('blur')} title="Smooth blur">Blur</button>
                    <button type="button" className={`btn ${style === 'pixelate' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        disabled={status !== 'ready'} onClick={() => setStyle('pixelate')} title="Mosaic / pixelate">Pixelate</button>
                </div>

                {/* Strength */}
                <label className="small text-muted d-flex align-items-center gap-2 m-0" style={{ whiteSpace: 'nowrap' }} title="How strong the blur / pixelation is">
                    <i className="fas fa-sliders-h"></i>
                    <span className="d-none d-xl-inline">Strength</span>
                    <input type="range" min={5} max={40} step={1}
                        value={Math.round(blurStrength * 100)}
                        disabled={status !== 'ready'}
                        onChange={(e) => setBlurStrength(Number(e.target.value) / 100)}
                        style={{ width: 96 }} aria-label="Blur strength" />
                </label>

                <span className="vr d-none d-md-inline"></span>

                {/* Region tools */}
                <button type="button" className={`btn btn-sm ${drawMode ? 'btn-secondary' : 'btn-outline-secondary'}`}
                    disabled={status !== 'ready'} onClick={() => setDrawMode(m => !m)}
                    data-help="redactor.draw"
                    title="Draw a blur area over a face the detector missed">
                    <i className="fas fa-vector-square me-1"></i>{drawMode ? 'Drawing…' : 'Draw'}
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary"
                    disabled={status !== 'ready' || regions.length === 0} onClick={blurAllFaces}
                    data-help="redactor.blur-all"
                    title="Blur every detected face at once">
                    <i className="fas fa-users me-1"></i>Blur all
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary"
                    disabled={status !== 'ready' || !regions.some(r => r.blurred)} onClick={clearAll}
                    title="Remove all blurring">
                    <i className="fas fa-eraser me-1"></i>Clear
                </button>
                {selectedId && (
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={removeSelected}
                        title="Delete the selected oval (or press Delete)">
                        <i className="fas fa-trash-alt me-1"></i>Remove
                    </button>
                )}

                {/* Actions (pushed to the right) */}
                <div className="ms-auto d-flex align-items-center gap-2">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>Cancel</button>
                    <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={handleApply}
                        data-help="redactor.apply"
                        title="Save this photo with the blurring applied">
                        <i className="fas fa-check me-1"></i>Apply &amp; use photo
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default PhotoRedactor;
